import { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../services/api';
import { useRole, hasMinimumRole } from '../hooks/useRole';

interface DimensionRecord {
  Er_aktiv: boolean;
  Sist_endret: string;
  [key: string]: unknown;
}

interface DimensionResponse {
  data: DimensionRecord[];
}

interface TabConfig {
  type: string;
  label: string;
  columns: GridColDef[];
  pkField: string;
}

const tabs: TabConfig[] = [
  {
    type: 'sektor', label: 'Sektor', pkField: 'SektorID',
    columns: [
      { field: 'SektorID', headerName: 'ID', width: 100 },
      { field: 'SektorNavn', headerName: 'Navn', width: 250, flex: 1 },
      { field: 'Sektorleder', headerName: 'Sektorleder', width: 200 },
    ],
  },
  {
    type: 'rammeomraade', label: 'Rammeområde', pkField: 'RammeomraadeID',
    columns: [
      { field: 'RammeomraadeID', headerName: 'ID', width: 100 },
      { field: 'RammeomraadeNavn', headerName: 'Navn', width: 250, flex: 1 },
      { field: 'SektorID', headerName: 'Sektor', width: 100 },
    ],
  },
  {
    type: 'ansvar', label: 'Ansvar', pkField: 'Ansvar',
    columns: [
      { field: 'Ansvar', headerName: 'Kode', width: 80 },
      { field: 'AnsvarNavn', headerName: 'Navn', width: 250, flex: 1 },
      { field: 'RammeomraadeID', headerName: 'Rammeområde', width: 120 },
      { field: 'Leder', headerName: 'Leder', width: 200 },
    ],
  },
  {
    type: 'konto', label: 'Konto', pkField: 'Konto',
    columns: [
      { field: 'Konto', headerName: 'Kode', width: 80 },
      { field: 'KontoNavn', headerName: 'Navn', width: 250, flex: 1 },
      { field: 'Kontotype', headerName: 'Type', width: 160 },
    ],
  },
  {
    type: 'tjeneste', label: 'Tjeneste', pkField: 'Tjeneste',
    columns: [
      { field: 'Tjeneste', headerName: 'Kode', width: 80 },
      { field: 'TjenesteNavn', headerName: 'Navn', width: 250, flex: 1 },
      { field: 'Tjenesteomraade', headerName: 'Område', width: 200 },
    ],
  },
  {
    type: 'prosjekt', label: 'Prosjekt', pkField: 'Prosjekt',
    columns: [
      { field: 'Prosjekt', headerName: 'Kode', width: 120 },
      { field: 'ProsjektNavn', headerName: 'Navn', width: 250, flex: 1 },
      { field: 'Prosjektleder', headerName: 'Prosjektleder', width: 200 },
      { field: 'Startdato', headerName: 'Start', width: 110 },
      { field: 'Sluttdato', headerName: 'Slutt', width: 110 },
    ],
  },
  {
    type: 'periodisering', label: 'Periodisering', pkField: 'Periodisering',
    columns: [
      { field: 'Periodisering', headerName: 'Kode', width: 80 },
      { field: 'Beskrivelse', headerName: 'Beskrivelse', width: 300, flex: 1 },
      { field: 'Antall_perioder', headerName: 'Perioder/år', width: 110, type: 'number' },
      { field: 'Startmaaned', headerName: 'Startmåned', width: 110, type: 'number' },
    ],
  },
];

export function DimensionsPage() {
  const [activeTab, setActiveTab] = useState(0);
  const role = useRole();
  const isEditor = hasMinimumRole(role, 'Editor');
  const queryClient = useQueryClient();

  const currentTab = tabs[activeTab];

  const { data, isLoading, error } = useQuery({
    queryKey: ['dimensions', currentTab.type],
    queryFn: () => apiGet<DimensionResponse>(`/dimensions/${currentTab.type}`),
  });

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 2 }}>Dimensjoner</Typography>

      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.type} label={tab.label} />
        ))}
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Kunne ikke laste dimensjoner'}
        </Alert>
      )}

      <DataGrid
        rows={data?.data ?? []}
        columns={currentTab.columns}
        getRowId={(row) => row[currentTab.pkField] as string}
        loading={isLoading}
        disableRowSelectionOnClick
        autoHeight
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
        sx={{ bgcolor: 'background.paper' }}
      />
    </Box>
  );
}
